import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANG_STORAGE_KEY } from "../i18n";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Confirmation page reached when a user clicks the language-toggle link
 * inside a transactional email footer.
 *
 * Flow:
 *  1. Email footer contains a signed URL → `GET /api/auth/language-switch?token=…`
 *  2. Backend verifies HMAC, updates `users.language`, then 302-redirects to
 *     this page (`/idioma-cambiado?lang=es|en` or `?error=1`).
 *  3. Here we sync the i18n state + localStorage to match the new language,
 *     show a confirmation in the *new* language, and offer a CTA back home.
 *
 * Public route — no auth required (the token is the proof).
 */
export default function LanguageSwitchedPage() {
  const [params] = useSearchParams();
  const { i18n, t } = useTranslation();
  const lang = (params.get("lang") || "").slice(0, 2);
  const error = params.get("error");

  useEffect(() => {
    if (lang === "es" || lang === "en") {
      i18n.changeLanguage(lang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, lang);
      } catch (_e) {
        /* skip */
      }
    }
  }, [lang, i18n]);

  if (error) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-4"
        data-testid="lang-switched-error"
      >
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold mb-2">
            {i18n.language?.startsWith("en")
              ? "We couldn't apply the change"
              : "No pudimos aplicar el cambio"}
          </h1>
          <p className="text-sm text-neutral-600 mb-6">
            {i18n.language?.startsWith("en")
              ? "The language-switch link is invalid or has expired. You can change your language from your profile."
              : "El enlace de cambio de idioma es inválido o expiró. Puedes cambiar tu idioma desde tu perfil."}
          </p>
          <Link to="/profile">
            <Button data-testid="lang-switched-profile-cta">
              {t("nav.profile")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-4"
      data-testid="lang-switched-success"
    >
      <div className="max-w-md text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
          {t("common.languageUpdated")}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {lang === "en"
            ? "From now on, your RivoNes emails, SMS and notifications will arrive in English."
            : "A partir de ahora, tus correos, SMS y notificaciones de RivoNes llegarán en español."}
        </p>
        <Link to="/">
          <Button data-testid="lang-switched-home-cta" className="bg-terracotta hover:bg-terracotta/90">
            {lang === "en" ? "Back to home" : "Volver al inicio"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
